<div class="form-field has-icon"
     [class.has-suffix]="suffixIcon || suffixText"
     [class.has-prefix]="prefixIcon"
     [class.has-error]="showError"
     [class]="'number-align-' + numberAlignment">
  <!-- `for` is wired only when the caller supplied an id — that id is also what
       lands on the native control, so the two stay in sync. -->
  <label *ngIf="label" [attr.for]="id || null">{{ label }} <span *ngIf="isOptional" class="is-optional">(Optional)</span></label>

  <!-- TEXT / PASSWORD / NUMBER / DATE -->
  <div class="input-wrapper" *ngIf="type !== 'select' && type !== 'textarea'">
    <span class="prefix-icon" *ngIf="prefixIcon">
      <ui-svg-icon [svgIcon]="prefixIcon" [size]="suffixIconSize"></ui-svg-icon>
    </span>
    <!-- Text / Password / Number -->
    <input
      *ngIf="type === 'text' || type === 'password' || type === 'number' || type === 'email' || type === 'url'"
      #inputElement
      [type]="type"
      [attr.id]="id || null"
      [attr.data-testid]="testId || null"
      [placeholder]="placeholder"
      [value]="value"
      (input)="onNativeInput($event)"
      (focus)="onFocus()"
      (blur)="onBlur()"
      (keydown)="onKeyDown($event)"
      [min]="min"
      [max]="max"
      [step]="step"
      [disabled]="isDisabled"
      [class.has-prefix]="prefixIcon"
      [attr.autocomplete]="autocomplete || null"
      [attr.inputmode]="inputMode || null"
      [attr.maxlength]="maxLength"
    />

    <!-- Datepicker -->
     <ng-container *ngIf="type === 'datepicker'">
      <input
        *ngIf="type === 'datepicker'"
        #inputElement
        matInput
        [attr.data-testid]="testId || null"
        [placeholder]="placeholder"
        [value]="value"
        (dateChange)="onDateChange($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
        readonly
        [matDatepicker]="picker"
        [min]="minDate"
        [max]="maxDate"
        (click)="picker.open()"
        [disabled]="isDisabled"
        class="datepicker-input"
        [class.has-prefix]="prefixIcon"
      />
      
      <span class="clear-datepicker" *ngIf="value" (click)="clearDatePicker($event)">
        <ui-svg-icon svgIcon="close-cc" size="large"></ui-svg-icon>
      </span>
    </ng-container>
    <span class="suffix-icon" *ngIf="suffixIcon && !suffixIconClickable">
      <ui-svg-icon [svgIcon]="suffixIcon" [size]="suffixIconSize"></ui-svg-icon>
    </span>
    <!-- Clickable variant: the suffix icon acts as its own control (password
         reveal toggle), so it has to be a real button with an accessible name. -->
    <button
      *ngIf="suffixIcon && suffixIconClickable"
      type="button"
      class="suffix-icon"
      [attr.aria-label]="suffixIconAriaLabel || null"
      [attr.aria-pressed]="suffixIconPressed"
      (click)="suffixIconClick.emit()"
    >
      <ui-svg-icon [svgIcon]="suffixIcon" [size]="suffixIconSize"></ui-svg-icon>
    </button>
    <span class="suffix-icon" *ngIf="suffixText && !suffixIcon">
      {{ suffixText }}
    </span>
    <mat-datepicker #picker></mat-datepicker>
  </div>

  <!-- TEXTAREA -->
  <textarea
    *ngIf="type === 'textarea'"
    [attr.id]="id || null"
    [attr.data-testid]="testId || null"
    [placeholder]="placeholder"
    [value]="value"
    (input)="onNativeInput($event)"
    (focus)="onFocus()"
    (blur)="onBlur()"
    [disabled]="isDisabled"
    rows="4"
  ></textarea>

  <!-- SELECT (single) -->
  <select
    *ngIf="type === 'select' && !multiple && !hasOptionIcons"
    [attr.id]="id || null"
    [attr.data-testid]="testId || null"
    [ngModel]="value"
    (ngModelChange)="onChange($event)"
    (focus)="onFocus()"
    (blur)="onBlur()"
    [disabled]="isDisabled"
  >
    <option *ngIf="placeholder" [ngValue]="null" disabled hidden>
      {{ placeholder }}
    </option>
    <option *ngFor="let option of options" [ngValue]="option.value" [disabled]="option.disabled">
      {{ option.label }}
    </option>
  </select>

  <!-- SELECT (single custom dropdown with optional icons) -->
  <!-- testId sits on the trigger, not the wrapper: everywhere else it lands on the
       control the user operates, so a test that clicks it opens the dropdown
       rather than hitting an inert container. -->
  <div *ngIf="type === 'select' && !multiple && hasOptionIcons" class="custom-single-select">
    <div class="single-select-trigger" [attr.data-testid]="testId || null" [class.disabled]="isDisabled" (click)="toggleDropdown($event)">
      <span class="selected-text" [title]="getSingleSelectDisplayText()" [attr.data-placeholder]="placeholder || emptySelectText">{{ getSingleSelectDisplayText() }}</span>
      <span class="dropdown-arrow" [class.rotated]="isDropdownOpen">
        <ui-svg-icon svgIcon="arrow_down" size="small"></ui-svg-icon>
      </span>
    </div>

    <div *ngIf="isDropdownOpen" class="dropdown-options"
         [style.top.px]="dropdownPosition.top"
         [style.left.px]="dropdownPosition.left"
         [style.width.px]="dropdownPosition.width">
      <div *ngFor="let option of options; trackBy: trackByValue"
           class="dropdown-option"
           [class.disabled]="option.disabled"
           [class.selected]="value === option.value"
           (click)="selectSingleOption(option)">
        <span class="option-icon" *ngIf="option.icon">
          <ui-svg-icon [svgIcon]="option.icon" size="small"></ui-svg-icon>
        </span>
        <span class="option-label" [title]="option.label">{{ option.label }}</span>
      </div>
    </div>
  </div>

  <!-- SELECT (multiple custom dropdown) -->
  <div *ngIf="type === 'select' && multiple" class="custom-multi-select">
    <div class="multi-select-trigger" [attr.data-testid]="testId || null" [class.disabled]="isDisabled" (click)="toggleDropdown($event)">
      <span class="selected-text" [title]="getDisplayText()" [attr.data-placeholder]="placeholder || emptySelectText">{{ getDisplayText() }}</span>
      <span class="dropdown-arrow" [class.rotated]="isDropdownOpen">
        <ui-svg-icon svgIcon="arrow_down" size="small"></ui-svg-icon>
      </span>
    </div>

    <div *ngIf="isDropdownOpen" class="dropdown-options"
         [style.top.px]="dropdownPosition.top"
         [style.left.px]="dropdownPosition.left"
         [style.width.px]="dropdownPosition.width">
      <input
        *ngIf="searchable"
        type="text"
        class="dropdown-search"
        [placeholder]="searchPlaceholder"
        [value]="searchTerm"
        (input)="onSearchInput($any($event.target).value)"
      />
      <div *ngFor="let option of (searchable ? filteredOptions : options); trackBy: trackByValue"
           class="dropdown-option"
           [class.disabled]="option.disabled"
           [class.selected]="isOptionSelected(option.value)"
           (click)="toggleOption(option.value, !!option.disabled)">
        <span class="option-check-icon">
          <ui-svg-icon *ngIf="isOptionSelected(option.value)" svgIcon="check-cc" size="medium"></ui-svg-icon>
        </span>
        <span class="option-icon" *ngIf="option.icon">
          <ui-svg-icon [svgIcon]="option.icon" size="small"></ui-svg-icon>
        </span>
        <span class="option-label" [title]="option.label">{{ option.label }}</span>
      </div>
    </div>
  </div>

  <!-- Errors -->
  <div class="error" *ngIf="showError && !hideErrors">
    <small *ngFor="let error of errors" [title]="error">{{ error }}</small>
  </div>
</div>
